// pkg189 — Agent role (AI-agent staff accounts, D-AG6): purple Agent badge + role option, no bar number, a hint under the role select explaining what an agent account is for and what it can never do — delivered 2026-10-31
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg51 — wheel-scroll can no longer change a number input's value (a scroll notch = one $0.01 step was how $300.00 became $299.98): every type="number" input blurs on wheel — delivered 2026-07-09
import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/adminApi';
import AdminUserPermissionsModal from '../components/AdminUserPermissionsModal';

const ROLES = [
  { value: 'owner',      label: 'Owner',      color: '#1a3a2a', bg: '#f0faf5' },
  { value: 'attorney',   label: 'Attorney',   color: '#2f5496', bg: '#f0f4ff' },
  { value: 'accounting', label: 'Accounting', color: '#5b3a8c', bg: '#f5f0fa' },
  { value: 'staff',      label: 'Staff',      color: '#555',    bg: '#f5f5f5' },
  { value: 'agent',      label: 'Agent',      color: '#534AB7', bg: '#EEEDFE' }, // pkg189 — AI-agent account
];

function RoleBadge({ role }) {
  const r = ROLES.find(x => x.value === role) || ROLES.find(x => x.value === 'staff');
  return (
    <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', padding: '2px 8px', borderRadius: 3,
      background: r.bg, color: r.color, border: `1px solid ${r.color}33`, fontWeight: 600 }}>
      {r.label}
    </span>
  );
}

// Roles that do NOT need a bar number (don't sign filings).
const ROLES_WITHOUT_BAR = new Set(['staff', 'accounting', 'agent']); // pkg189 — agents never sign filings

export default function AdminStaffPage() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [adding,  setAdding]  = useState(false);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', username: '', password: '', role: 'staff', default_hourly_rate: '', bar_number: '' });

  useEffect(() => {
    adminApi.getAdminUsers()
      .then(data => { setUsers(data); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  }, []);

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleCreate(e) {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      const user = await adminApi.createAdminUser(form);
      setUsers(prev => [...prev, user]);
      setForm({ name: '', email: '', username: '', password: '', role: 'staff', default_hourly_rate: '', bar_number: '' });
      setAdding(false);
      setSuccess(`${user.name} added as ${user.role}.`);
    } catch (err) { setError(err.message); }
  }

  async function handleUpdateRole(id, role) {
    try {
      await adminApi.updateAdminUser(id, { role });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
      setEditing(null);
    } catch (err) { setError(err.message); }
  }

  function handleUserUpdated(updated) {
    setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
    setManaging(prev => prev && prev.id === updated.id ? { ...prev, ...updated } : prev);
  }

  // Modal calls this when the user is deleted from inside it. We yank the
  // row from the list and close the modal.
  function handleUserDeleted(deletedId, deletedName) {
    setUsers(prev => prev.filter(u => u.id !== deletedId));
    setManaging(null);
    setSuccess(`${deletedName} removed.`);
  }

  // Tight horizontal padding so all 8 columns fit comfortably in a typical
  // desktop card width without forcing a scrollbar. Headers don't need
  // whiteSpace:nowrap — they're short enough to fit on one line on their
  // own, and allowing wrap means narrower screens don't break the layout.
  const thStyle = { padding: '8px 8px', textAlign: 'left', fontSize: 11, fontFamily: 'Arial, sans-serif',
    textTransform: 'uppercase', letterSpacing: '0.04em', color: '#fff', fontWeight: 600, background: '#0F1838' };
  const tdStyle = { padding: '10px 8px', fontSize: 13, fontFamily: 'Arial, sans-serif',
    borderBottom: '1px solid #eee', verticalAlign: 'middle' };

  return (
    <div>
      <h1 className="page-title">Staff Management</h1>
      <p className="page-subtitle">
        Manage who has access to the admin portal and what role they have.
        Click <strong>Manage</strong> on any staff member to edit their profile, reset their password, or remove them.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Add staff member */}
      {adding ? (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ fontSize: 15, marginBottom: 16 }}>Add Staff Member</div>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
              </div>
            </div>
            <div className="form-row three">
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={form.username}
                  onChange={e => set('username', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="lowercase, no spaces" required />
              </div>
              <div className="form-group">
                <label>Temporary Password</label>
                <input type="text" value={form.password} onChange={e => set('password', e.target.value)}
                  placeholder="At least 8 characters" required />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={form.role} onChange={e => set('role', e.target.value)}>
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Default Hourly Rate</label>
                <input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" step="0.01" value={form.default_hourly_rate}
                  onChange={e => set('default_hourly_rate', e.target.value)} placeholder="e.g. 300.00" />
              </div>
            </div>
            {/* pkg189 — what an Agent account is, shown only when that role is picked. */}
            {form.role === 'agent' && (
              <div style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#534AB7', background: '#EEEDFE',
                border: '1px solid #534AB733', borderRadius: 3, padding: '8px 10px', marginBottom: 12, lineHeight: 1.5 }}>
                An <strong>Agent</strong> account is for an AI assistant (Grok Bot, Claude, and the like) that signs in to work on
                the firm's behalf. Everything it does is tagged in the Activity Log. By default it can view clients and billing,
                log time and expenses, add notes and dates, and leave invoices in draft. It can never delete, send, issue
                invoices, touch trust or payments, or change staff or firm settings — even if those toggles are turned on.
              </div>
            )}
            {/* Bar number is required for owners and attorneys (they sign filings).
                Hidden entirely for staff, accounting, and agent roles. */}
            {!ROLES_WITHOUT_BAR.has(form.role) && (
              <div className="form-row">
                <div className="form-group">
                  <label>Bar Number <span style={{ color: '#B85042' }}>*</span></label>
                  <input type="text" value={form.bar_number}
                    onChange={e => set('bar_number', e.target.value)}
                    placeholder="e.g. BPR # 024983 or State Bar No. 123456"
                    required />
                </div>
              </div>
            )}
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => { setAdding(false); setError(''); }}>Cancel</button>
              <button type="submit" className="btn btn-primary">Add Staff Member</button>
            </div>
          </form>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={() => setAdding(true)} style={{ marginBottom: 20 }}>
          + Add Staff Member
        </button>
      )}

      {/* Staff list */}
      {loading ? (
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading...</div>
      ) : (
        <>
          {/* DESKTOP TABLE
              Action column uses width:1 + whiteSpace:nowrap so it shrinks to
              exactly the size of the Manage button — frees up room for
              everything else. Card keeps overflow:hidden so no scrollbar. */}
          <div className="desktop-only table-wrap">
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Username</th>
                    <th style={thStyle}>Bar #</th>
                    <th style={thStyle}>Rate</th>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Added</th>
                    <th style={{ ...thStyle, width: 1 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={8} style={{ ...tdStyle, color: '#888', textAlign: 'center' }}>
                      No staff members yet.
                    </td></tr>
                  )}
                  {users.map((u, i) => (
                    <tr key={u.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{u.name}</td>
                      <td style={tdStyle}>{u.email}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{u.username}</td>
                      <td style={tdStyle}>
                        {ROLES_WITHOUT_BAR.has(u.role)
                          ? <span style={{ color: '#aaa' }}>—</span>
                          : (u.bar_number
                              ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.bar_number}</span>
                              : <span style={{ color: '#B85042', fontSize: 11 }}>missing</span>)}
                      </td>
                      <td style={tdStyle}>{u.default_hourly_rate ? `$${parseFloat(u.default_hourly_rate).toFixed(2)}/hr` : <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {editing === u.id ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <select defaultValue={u.role}
                              onChange={e => handleUpdateRole(u.id, e.target.value)}
                              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 3 }}>
                              {ROLES.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <button onClick={() => setEditing(null)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 16 }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <RoleBadge role={u.role} />
                            <button onClick={() => setEditing(u.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 11, textDecoration: 'underline', fontFamily: 'Arial, sans-serif' }}>
                              change
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: '#888', fontSize: 12 }}>
                        {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ ...tdStyle, width: 1, whiteSpace: 'nowrap', textAlign: 'right', paddingRight: 14 }}>
                        <button className="btn btn-sm btn-primary" onClick={() => setManaging(u)}>Manage</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE CARDS — single Manage button mirrors desktop. */}
          <div className="mobile-only">
            {users.length === 0 && (
              <div className="card" style={{ textAlign: 'center', color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
                No staff members yet.
              </div>
            )}
            {users.map(u => (
              <div key={u.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: '#555', fontFamily: 'Arial, sans-serif', marginBottom: 2,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                    <div style={{ fontSize: 12, color: '#888', fontFamily: 'monospace', marginBottom: 8 }}>@{u.username}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <RoleBadge role={u.role} />
                      {u.default_hourly_rate && (
                        <span style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#555' }}>
                          ${parseFloat(u.default_hourly_rate).toFixed(2)}/hr
                        </span>
                      )}
                      {!ROLES_WITHOUT_BAR.has(u.role) && (
                        u.bar_number
                          ? <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#555' }}>
                              {u.bar_number}
                            </span>
                          : <span style={{ fontSize: 11, color: '#B85042', fontFamily: 'Arial, sans-serif' }}>
                              ⚠ no bar #
                            </span>
                      )}
                      <span style={{ fontSize: 11, color: '#aaa', fontFamily: 'Arial, sans-serif' }}>
                        Added {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <button className="btn btn-sm btn-primary" onClick={() => setManaging(u)}>Manage</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Manage modal: profile + permissions + reset password + delete */}
      {managing && (
        <AdminUserPermissionsModal
          user={managing}
          onClose={() => setManaging(null)}
          onUserUpdated={handleUserUpdated}
          onUserDeleted={handleUserDeleted}
        />
      )}
    </div>
  );
}
